import { useState } from 'react';
import type { ValidationErrors } from '@frontend/utils/validation';
import { patientCareProviderEditSchema } from '@common/validation/patient';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import type { IPatient } from '../../../../../common/interfaces/patient';
import { CareProviderSelection } from '../../../elements/CareProviderSelection';
import { Button } from '../../../elements/common';
import { Modal } from '../../../elements/modals/Modal';

interface IPatientEditCareProviderModalProps {
    isOpen: boolean;
    onClose: () => Promise<void>;
    onSave: () => Promise<void>;
    patient: IPatient;
}

export function PatientEditCareProviderModal(props: IPatientEditCareProviderModalProps) {
    const translate = getSelectedLanguage();
    const [error, setError] = useState<ValidationErrors>(emptyValidationErrors);
    const [careProviderPractitionerId, setCareProviderPractitionerId] = useState<string | null>(
        props.patient.careProviderPractitionerId || null
    );
    const [careProviderOrganizationId, setCareProviderOrganizationId] = useState<string | null>(
        props.patient.careProviderOrganizationId || null
    );

    return (
        <Modal
            actionButtons={[
                <Button
                    color="secondary"
                    large
                    onClick={props.onClose}
                    outlined
                >
                    {translate.common.cancel}
                </Button>,
                <Button
                    color="secondary"
                    disabled={!careProviderOrganizationId}
                    large
                    onClick={async () => await onSave()}
                >
                    {translate.patient.editCareProvider}
                </Button>,
            ]}
            isOpen={props.isOpen}
            onClose={props.onClose}
            size="medium"
            title={translate.patient.careProviderEdit}
        >
            <CareProviderSelection
                automation="choosePractitioner"
                error={error.get('careProviderPractitionerId') || error.get('careProviderOrganizationId')}
                formlabeltext={translate.patient.whoIsCareProvider}
                initialOrganizationId={careProviderOrganizationId}
                initialPractitionerId={careProviderPractitionerId}
                isInModal={true}
                onChange={async (organizationId, practitionerId) => {
                    setCareProviderPractitionerId(practitionerId);
                    setCareProviderOrganizationId(organizationId);
                }}
                onDoValidate={async () => setError(emptyValidationErrors)}
                patient={props.patient}
                required
            />
        </Modal>
    );

    async function onSave() {
        const { item, errors } = validate(patientCareProviderEditSchema, {
            careProviderPractitionerId,
            careProviderOrganizationId,
        });
        if (errors.size > 0) {
            setError(errors);

            return;
        }

        await fetchJson(`/api/patients/${props.patient.id}/careProvider`, 'POST', item);
        await props.onSave();
    }
}
